import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    Move,
    Pass,
    Sound10Ru,
    Sound10Zh,
    Sound1Ru,
    Sound1Zh,
    Sound2Ru,
    Sound2Zh,
    Sound3Ru,
    Sound3Zh,
    Sound4Ru,
    Sound4Zh,
    Sound5Ru,
    Sound5Zh,
} from '../assets';
import { useDidMount } from '@/shared/hooks/useDidMount';
import { useLocalStorage } from '@/shared/utils';
import { logServicePostLog } from '@/api';

// Структура звуков
const SOUNDS_MAP = {
    move: Move,
    pass: Pass,
    time: {
        ru: {
            10: Sound10Ru,
            5: Sound5Ru,
            4: Sound4Ru,
            3: Sound3Ru,
            2: Sound2Ru,
            1: Sound1Ru,
        },
        zh: {
            10: Sound10Zh,
            5: Sound5Zh,
            4: Sound4Zh,
            3: Sound3Zh,
            2: Sound2Zh,
            1: Sound1Zh,
        },
    },
} as const;

const SOUND_WEIGHTS: Partial<Record<string, number>> = {
    move: 0.5,
};

type Leaves<T> = T extends object
    ? { [K in keyof T]: `${Exclude<K, symbol>}${Leaves<T[K]> extends never ? '' : `.${Leaves<T[K]>}`}` }[keyof T]
    : never;

type SoundPath = Leaves<typeof SOUNDS_MAP>;

interface AudioContextType {
    playSound: (path: SoundPath) => void;
    playSoundFile: (url: string, volume?: number) => void;
    volume: number;
    setVolume: (value: number) => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

export const AudioProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { i18n } = useTranslation();
    const location = useLocation();

    const audioContextRef = useRef<AudioContext | null>(null);
    const gainNodeRef = useRef<GainNode | null>(null);
    const buffersRef = useRef<Map<string, AudioBuffer>>(new Map());
    const loadingPromisesRef = useRef<Map<string, Promise<AudioBuffer | null>>>(new Map());
    const timerSourceRef = useRef<AudioBufferSourceNode | null>(null);

    const [volume, setVolumeState] = useLocalStorage('game-volume', 0.5);

    const getOrFetchBuffer = useCallback(async (path: string, sourceUrl?: string): Promise<AudioBuffer | null> => {
        const ctx = audioContextRef.current;
        if (!ctx) return null;

        if (buffersRef.current.has(path)) {
            return buffersRef.current.get(path)!;
        }

        if (loadingPromisesRef.current.has(path)) {
            return loadingPromisesRef.current.get(path)!;
        }

        let url: unknown = sourceUrl;
        if (!url) {
            const keys = path.split('.');
            url = SOUNDS_MAP;
            for (const key of keys) {
                url = (url as Record<string, unknown>)?.[key];
            }
        }

        if (typeof url !== 'string') return null;

        const loadPromise = (async () => {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await ctx.decodeAudioData(arrayBuffer);

                buffersRef.current.set(path, audioBuffer);
                return audioBuffer;
            } catch (err) {
                logServicePostLog({ body: { message: `Ошибка ленивой загрузки звука [${path}]: ${err}` } });
                return null;
            } finally {
                loadingPromisesRef.current.delete(path);
            }
        })();

        loadingPromisesRef.current.set(path, loadPromise);
        return loadPromise;
    }, []);

    // Логика предзагрузки на основе текущего роута
    useEffect(() => {
        const path = location.pathname;

        const isGameView = /^\/(demo|viewer\/|broadcast\/|server\/)/.test(path);
        const isServerRoom = /^\/server\//.test(path);

        if (isGameView) {
            getOrFetchBuffer('move');
        }

        if (isServerRoom) {
            getOrFetchBuffer('pass');

            const lang = i18n.language === 'zh' ? 'zh' : 'ru';
            const timeTicks = [1, 2, 3, 4, 5, 10] as const;

            timeTicks.forEach((num) => {
                getOrFetchBuffer(`time.${lang}.${num}` as SoundPath);
            });
        }
    }, [location.pathname, i18n.language, getOrFetchBuffer]);

    useDidMount(() => {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioContextClass();
        audioContextRef.current = ctx;

        const gainNode = ctx.createGain();
        gainNode.gain.value = volume;
        gainNode.connect(ctx.destination);
        gainNodeRef.current = gainNode;

        const unlock = () => {
            if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
        };

        window.addEventListener('pointerdown', unlock);
        window.addEventListener('keydown', unlock);

        return () => {
            ctx.close();
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    });

    const setVolume = useCallback(
        (value: number) => {
            const normalizedValue = Math.max(0, Math.min(1, value));
            setVolumeState(normalizedValue);

            if (gainNodeRef.current && audioContextRef.current) {
                const ctx = audioContextRef.current;
                gainNodeRef.current.gain.setTargetAtTime(normalizedValue, ctx.currentTime, 0.01);
            }
        },
        [setVolumeState]
    );

    const playAudio = useCallback(
        async (path: string, sourceUrl?: string, isTimerSound = false, useMasterVolume = true, volume = 1) => {
            const ctx = audioContextRef.current;
            const masterGainNode = gainNodeRef.current;

            if (!ctx || (useMasterVolume && !masterGainNode)) return;

            const buffer = await getOrFetchBuffer(path, sourceUrl);
            if (!buffer) return;

            if (timerSourceRef.current && isTimerSound) {
                try {
                    timerSourceRef.current.stop();
                } catch {
                    /* игнорируем */
                }
                timerSourceRef.current = null;
            }

            if (ctx.state === 'suspended') {
                try {
                    await ctx.resume();
                } catch {
                    return;
                }
            }
            if (ctx.state !== 'running') return;

            const source = ctx.createBufferSource();
            source.buffer = buffer;

            const weight = (SOUND_WEIGHTS[path] ?? 1.0) * Math.max(0, Math.min(1, volume));
            const localGain = ctx.createGain();
            localGain.gain.value = weight;

            source.connect(localGain);
            localGain.connect(useMasterVolume ? masterGainNode! : ctx.destination);

            if (isTimerSound) {
                timerSourceRef.current = source;
            }

            source.start(0);

            source.onended = () => {
                if (timerSourceRef.current === source) {
                    timerSourceRef.current = null;
                }
            };
        },
        [getOrFetchBuffer]
    );

    const playSound = useCallback(
        (path: SoundPath) => void playAudio(path, undefined, path.startsWith('time')),
        [playAudio]
    );

    const playSoundFile = useCallback(
        (url: string, volume = 1) => void playAudio(`file:${url}`, url, false, false, volume),
        [playAudio]
    );

    return (
        <AudioContext.Provider value={{ playSound, playSoundFile, volume, setVolume }}>
            {children}
        </AudioContext.Provider>
    );
};

export const useAudio = () => {
    const context = useContext(AudioContext);
    if (!context) throw new Error('useAudio must be used within AudioProvider');
    return context;
};
